/**
 * Promotions API service for fetching machine-specific promotions
 */

import { API_BASE_URL } from '../config/api.config';

/**
 * Fetches active promotions for a specific machine
 * @param {string} clientId - Client identifier (e.g., 'sanven')
 * @param {string|number} storeId - Machine/store identifier
 * @param {boolean} preview - Whether to fetch preview promotions
 * @returns {Promise<Array>} Array of promotion objects
 */
export const getPromotions = async (clientId, storeId, preview = false) => {
  try {
    const url = preview 
      ? `${API_BASE_URL}/promotions?clientId=${clientId}&storeId=${storeId}&preview=true`
      : `${API_BASE_URL}/promotions?clientId=${clientId}&storeId=${storeId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const promotions = await response.json();
    
    return processPromotions(promotions);
  } catch (error) {
    throw error;
  }
};

/**
 * Processes raw promotion data and extracts useful information
 * @param {Array} rawPromotions - Raw promotions from API
 * @returns {Array} Processed promotions with extracted data
 */
const processPromotions = (rawPromotions) => {
  return rawPromotions.map(promotion => ({
    ...promotion,
    // Determine promotion type based on title
    type: promotion.title.includes('🔥') ? 'combo' : 'discount',
    // Extract selection code from message
    selectionCode: extractSelectionCode(promotion.message),
    // Extract machine name from message
    machineName: extractMachineName(promotion.message),
    // Clean title (remove emojis)
    cleanTitle: promotion.title.replace(/🔥|💰/g, '').trim(),
    // Extract discount percentage
    discountPercentage: extractDiscountPercentage(promotion.subtitle)
  }));
};

/**
 * Extracts selection code from promotion message
 * @param {string} message - Promotion message
 * @returns {string} Selection code or 'N/A'
 */
const extractSelectionCode = (message) => {
  if (!message) return 'N/A';
  
  // Look for patterns like "Cod. Selección 66" or "Code: 22"
  const codeMatch = message.match(/(?:Cod\.?\s*(?:Selección|Selection|Code)?\s*[:#]?\s*)(\d+)/i);
  if (codeMatch) {
    return codeMatch[1];
  }
  
  // Fallback: look for any number after "Cod" or "Code"
  const fallbackMatch = message.match(/(?:cod|code)\D*(\d+)/i);
  return fallbackMatch ? fallbackMatch[1] : 'N/A';
};

/**
 * Extracts machine name from promotion message
 * @param {string} message - Promotion message
 * @returns {string} Machine name or 'Vending Machine'
 */
const extractMachineName = (message) => {
  if (!message) return 'Vending Machine';
  
  // Look for patterns like "Máquina: NAME" or "Machine: NAME"
  const machineMatch = message.match(/(?:Máquina|Machine):\s*(.+?)(?:\n|$)/i);
  if (machineMatch) {
    return machineMatch[1].trim();
  }
  
  return 'Vending Machine';
};

/**
 * Extracts discount percentage from subtitle
 * @param {string} subtitle - Promotion subtitle
 * @returns {string} Discount percentage or '0%'
 */
const extractDiscountPercentage = (subtitle) => {
  if (!subtitle) return '0%';
  
  const discountMatch = subtitle.match(/-?(\d+(?:\.\d+)?)%/);
  return discountMatch ? `${discountMatch[1]}%` : '0%';
};

/**
 * Cache promotions in localStorage for offline access
 * @param {string} storeId - Store identifier
 * @param {Array} promotions - Promotions to cache
 */
export const cachePromotions = (storeId, promotions) => {
  try {
    const cacheData = {
      timestamp: Date.now(),
      promotions,
      storeId
    };
    localStorage.setItem(`promotions_${storeId}`, JSON.stringify(cacheData));
  } catch (error) {
  }
};

/**
 * Get cached promotions for offline access
 * @param {string} storeId - Store identifier
 * @param {number} maxAge - Maximum cache age in milliseconds (default: 5 minutes)
 * @returns {Array|null} Cached promotions or null if not available/expired
 */
export const getCachedPromotions = (storeId, maxAge = 5 * 60 * 1000) => {
  try {
    const cached = localStorage.getItem(`promotions_${storeId}`);
    if (!cached) return null;
    
    const cacheData = JSON.parse(cached);
    const isExpired = Date.now() - cacheData.timestamp > maxAge;
    
    if (isExpired) {
      localStorage.removeItem(`promotions_${storeId}`);
      return null;
    }
    
    return cacheData.promotions;
  } catch (error) {
    return null;
  }
};